import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  GlobalFiltering,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import GlobalFilter from "../../../components/globalFilter";
import Tabelas from "../../../components/table";
import CSVButton from "../../../components/CSVButton";
import DeleteConfirmationModal from "../../../components/modalDelete";
import ErrorModal from "../../../components/modalError";

function BrokerTable({ data, onShowModal, onEditBroker, onDeleteBroker }) {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(data);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [brokerToDeleteId, setBrokerToDeleteId] = useState(null);
  const [brokerName, setBrokerName] = useState(null);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const handleDelete = (broker) => {
    setBrokerToDeleteId(broker.id);
    setBrokerName(broker.name);
    setShowDeleteConfirmationModal(true);
  };

  const confirmDelete = async () => {
    try {
      await onDeleteBroker(brokerToDeleteId);
      setTableData((prev) => prev.filter((r) => r.id !== brokerToDeleteId));
    } catch (err) {
      console.error(err);
      setError(t("Erro ao deletar esse broker"));
      setShowErrorModal(true);
    } finally {
      setShowDeleteConfirmationModal(false);
      setBrokerToDeleteId(null);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setError("");
  };

  const globalFilter = React.useCallback((rows, ids, query) => {
    const searchTerm = String(query);
    return rows.filter((row) => {
      const matches = ids.filter((id) => {
        const rowValue = row.values[id];
        if (searchTerm === "'") return true;
        if (searchTerm.startsWith("'") && searchTerm.endsWith("'")) {
          const exactSearchTerm = searchTerm.slice(1, -1);
          return String(rowValue) === exactSearchTerm;
        }
        let searchLower = searchTerm;
        if (searchTerm.startsWith("'")) searchLower = searchTerm.slice(1);
        return rowValue !== undefined
          ? String(rowValue).toLowerCase().includes(searchLower.toLowerCase())
          : false;
      });
      return matches.length > 0;
    });
  }, []);

  const exportToCSV = () => {
    if (!data) return;
    const authTypeMap = {
      N: "Sem Autenticação",
      P: "Autenticação por senha",
      S: "Autenticação por certificado",
    };
    return data.map((row) => ({
      Name: row.name,
      Codec: row.codec?.name,
      Host: row.host,
      Port: row.port,
      Topic: row.topic,
      Ativo: row.is_enabled ? t("Sim") : t("Não"),
      AuthType: authTypeMap[row.auth_type] || row.auth_type,
    }));
  };

  const columns = [
    {
      header: t("Name"),
      accessorKey: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: t("Codec"),
      accessorFn: (row) => row.codec?.name ?? "-",
      id: "codec",
    },

    { header: t("Host"), accessorKey: "host", cell: (info) => info.getValue() },
    { header: t("Port"), accessorKey: "port", cell: (info) => info.getValue() },
    {
      header: t("Topic"),
      accessorKey: "topic",
      cell: (info) => info.getValue(),
    },
    {
      header: t("Ativo"),
      accessorKey: "is_enabled",
      cell: (info) => (info.getValue() ? t("Sim") : t("Não")),
    },
    {
      header: t("Auth Type"),
      accessorKey: "auth_type",
      cell: (info) => {
        const authTypeMap = {
          N: "Sem Autenticação",
          P: "Autenticação por senha",
          S: "Autenticação por certificado",
        };
        return authTypeMap[info.getValue()] || info.getValue();
      },
    },
    {
      id: "acoes",
      header: (
        <div className="header-action">
          <button
            onClick={onShowModal}
            className="btn-table bi bi-plus-circle"
          ></button>
          <CSVButton data={exportToCSV()} filename={"Brokers.csv"} />
        </div>
      ),
      cell: (info) => (
        <div className="header-action">
          <button
            className="btn-table btn-editar bi bi-pencil-square"
            onClick={() => onEditBroker(info.row.original)}
            title={t("Editar")}
          ></button>
          <button
            className="btn-table btn-delete bi bi-trash3"
            onClick={() => handleDelete(info.row.original)}
            title={t("Deletar")}
          ></button>
        </div>
      ),
    },
  ];

  const table = useReactTable(
    {
      data: tableData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      globalFilter: globalFilter,
      getSortedRowModel: getSortedRowModel(),
      state: { sorting, pagination },
      onSortingChange: setSorting,
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      defaultColumn: {
        sortDescFirst: false,
      },
    },
    GlobalFiltering,
    getSortedRowModel,
  );

  const { setGlobalFilter } = table;

  return (
    <div className="p-2 div-table">
      <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />

      <Tabelas fixLastColumn={true} table={table} />
      <div className="pagination">
        <Button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          title="pagina anterior"
          className="pagination-btn"
        >
          <i className="bi bi-chevron-left pagination-icon"></i>
        </Button>
        <Button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          title="pagina seguinte"
          className="pagination-btn"
        >
          <i className="bi bi-chevron-right pagination-icon"></i>
        </Button>
        <select
          value={pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="pagination-select"
          aria-label="Select de paginação"
          id="select"
        >
          {[5, 10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
      </div>

      <ErrorModal
        showErrorModal={showErrorModal}
        handleCloseErrorModal={handleCloseErrorModal}
        errorMessage={error}
      />
      <DeleteConfirmationModal
        show={showDeleteConfirmationModal}
        onClose={() => setShowDeleteConfirmationModal(false)}
        onConfirm={confirmDelete}
        pagina={t("Broker")}
        name={brokerName}
      />
    </div>
  );
}

BrokerTable.propTypes = {
  data: PropTypes.array.isRequired,
  onShowModal: PropTypes.func.isRequired,
  onEditBroker: PropTypes.func.isRequired,
  onDeleteBroker: PropTypes.func.isRequired,
  deleteData: PropTypes.func,
};

export default BrokerTable;
